import React, { useState, useEffect } from "react";
import editorange from 'assets/images/icon/edit-orange.png';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompany, updateCompany } from "services/company";
import Input from "components/Input";
import Select from "components/Select";
import AddressAutocomplete from "components/AddressAutocomplete";

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const buildForm = (company) => ({
    legalName: company?.legalName || '',
    tradeName: company?.tradeName || '',
    ein: company?.ein || '',
    email: company?.email || '',
    phone: company?.phone || '',
    website: company?.website || '',
    street1: company?.addressLine1 || '',
    street2: company?.addressLine2 || '',
    city: company?.city || '',
    state: company?.state || '',
    zipcode: company?.zip || company?.zipcode || '',
    country: company?.country || 'US',
});

const GeneralInformationSection = ({ startInEditMode = false } = {}) => {
    const [isEditing, setIsEditing] = useState(!!startInEditMode);
    const [form, setForm] = useState(buildForm(null));
    const queryClient = useQueryClient();

    const { data: company, isLoading, isError } = useQuery({
        queryKey: ['company'],
        queryFn: getCompany,
    });

    useEffect(() => {
        if (company) setForm(buildForm(company));
    }, [company]);

    const mutation = useMutation({
        mutationFn: updateCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company'] });
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({
            legalName: form.legalName,
            ein: form.ein,
            tradeName: form.tradeName,
            email: form.email,
            phone: form.phone,
            website: form.website,
            address: {
                street1: form.street1,
                street2: form.street2,
                city: form.city,
                state: form.state,
                zipcode: form.zipcode,
                country: form.country,
            },
        });
    };

    const handleCancel = () => {
        setForm(buildForm(company));
        setIsEditing(false);
        mutation.reset();
    };

    if (isLoading) return <p className="text-center mt-4">Loading company information...</p>;
    if (isError) return <p className="text-danger mt-4">Failed to load company information.</p>;

    const address = [company?.addressLine1, company?.addressLine2, company?.city, company?.state, company?.zip, company?.country]
        .filter(Boolean).join(', ');

    if (isEditing) {
        return (
            <div>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                    <h2 className='sub-title'>Edit General Information</h2>
                </div>
                <form onSubmit={handleSave}>
                    <div className='border-bottom pb-4 mb-4'>
                        <p className='info-title mb-3'>General Information</p>
                        <div className='row g-3'>
                            <div className='col-lg-6 col-12'>
                                <Input label="Legal Name" fullWidth variant='flat' name='legalName' value={form.legalName} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="Trade Name" fullWidth variant='flat' name='tradeName' value={form.tradeName} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="EIN" fullWidth variant='flat' name='ein' value={form.ein} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="Email" fullWidth variant='flat' type='email' name='email' value={form.email} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="Phone" fullWidth variant='flat' name='phone' value={form.phone} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="Website" fullWidth variant='flat' name='website' value={form.website} onChange={handleChange} />
                            </div>
                        </div>
                    </div>
                    <div className='pb-4 mb-4'>
                        <p className='info-title mb-3'>Address</p>
                        <div className='row g-3'>
                            <div className='col-lg-6 col-12'>
                                <AddressAutocomplete
                                    label="Street 1"
                                    value={form.street1}
                                    onSelect={(a) => setForm(prev => ({ ...prev, street1: a.line1, city: a.city, state: a.state, zipcode: a.zip }))}
                                    onChange={(v) => setForm(prev => ({ ...prev, street1: v }))}
                                />
                            </div>
                            <div className='col-lg-6 col-12'>
                                <Input label="Street 2" fullWidth variant='flat' name='street2' value={form.street2} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 col-12'>
                                <Input label="City" fullWidth variant='flat' name='city' value={form.city} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 col-12'>
                                <Select label="State" fullWidth placeholder='Select state' options={US_STATES} name='state' value={form.state} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 col-12'>
                                <Input label="Zip Code" fullWidth variant='flat' name='zipcode' value={form.zipcode} onChange={handleChange} />
                            </div>
                        </div>
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mb-3">
                            {mutation.error?.response?.data?.message || 'Failed to update company. Please try again.'}
                        </p>
                    )}
                    <div className='d-flex gap-2'>
                        <button type='submit' className='button-orange py-2 px-4 rounded-pill' disabled={mutation.isPending}>
                            {mutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button type='button' className='btn btn-outline-secondary rounded-pill py-2 px-4' onClick={handleCancel} disabled={mutation.isPending}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div>
            <div className='d-flex align-items-center justify-content-between'>
                <h2 className='sub-title'>General Information</h2>
                <button className='border-0 bg-transparent' onClick={() => setIsEditing(true)}>
                    <img className='cursor-pointer' src={editorange} alt="edit" />
                </button>
            </div>
            <div className='general-information-section border-bottom pb-4 mb-4'>
                <div>
                    <p className='m-0 title'>Legal Name</p>
                    <p className='m-0 sub-titles'>{company?.legalName || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>Trade Name</p>
                    <p className='m-0 sub-titles'>{company?.tradeName || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>EIN</p>
                    <p className='m-0 sub-titles'>{company?.ein || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>Website</p>
                    <p className='m-0 sub-titles'>{company?.website || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>Status</p>
                    <p className='m-0 sub-titles'>{company?.status || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>Country</p>
                    <p className='m-0 sub-titles'>{company?.country || '—'}</p>
                </div>
                <div>
                    <p className='m-0 title'>Start Date</p>
                    <p className='m-0 sub-titles'>
                        {company?.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}
                    </p>
                </div>
            </div>
            <div>
                <h2 className='sub-title'>Contact Information</h2>
                <div className='general-information-section'>
                    <div>
                        <p className='m-0 title'>Address</p>
                        <p className='m-0 sub-titles address-details'>{address || '—'}</p>
                    </div>
                    <div>
                        <p className='m-0 title'>Email</p>
                        <p className='m-0 sub-titles'>{company?.email || '—'}</p>
                    </div>
                    <div>
                        <p className='m-0 title'>Phone</p>
                        <p className='m-0 sub-titles'>{company?.phone || '—'}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GeneralInformationSection;

